import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring, useMotionValue, useTransform } from 'framer-motion';
import { supabase } from '../supabaseClient';
import Footer from '../components/Footer';

/* ── tokens — match app menu exactly ── */
const GOLD = '#c8a04e';
const GA   = (a) => `rgba(200,160,78,${a})`;
const W    = (a) => `rgba(255,255,255,${a})`;

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing',  href: '#pricing'  },
  { label: 'FAQ',      href: '#faq'      },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open,     setOpen]     = useState(false);
  const [user,     setUser]     = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setUser(s?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  /* nav link style */
  const lk = {
    fontSize: 13,
    fontWeight: 500,
    color: W(0.45),
    textDecoration: 'none',
    padding: '6px 12px',
    borderRadius: 8,
    transition: 'color 0.14s',
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
  };
  const lOn  = e => { e.currentTarget.style.color = W(0.9); };
  const lOff = e => { e.currentTarget.style.color = W(0.45); };

  /* glass pill — mirrors app sel-item glassmorphism */
  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 20px',
    borderRadius: 10,
    background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
    border: `1px solid ${W(0.09)}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
    color: W(0.82),
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.02em',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
  };
  const pillOn  = e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.045) 55%, rgba(255,255,255,0.025) 100%)'; e.currentTarget.style.borderColor = W(0.15); e.currentTarget.style.color = W(1); };
  const pillOff = e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)'; e.currentTarget.style.borderColor = W(0.09); e.currentTarget.style.color = W(0.82); };

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        transition: 'background 0.35s ease, border-color 0.35s ease, backdrop-filter 0.35s ease',
        background:          scrolled ? 'rgba(0,0,0,0.75)' : 'transparent',
        backdropFilter:      scrolled ? 'blur(20px)'       : 'none',
        WebkitBackdropFilter:scrolled ? 'blur(20px)'       : 'none',
        borderBottom:        scrolled ? `1px solid ${W(0.07)}` : '1px solid transparent',
      }}>
        <div style={{
          maxWidth: 1120,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 24,
          padding: '0 32px',
          height: 68,
        }}>

          {/* LEFT — logo */}
          <Link to="/">
            <img src="/athena_logo.png" alt="Athena" style={{ height: 64, width: 64, display: 'block', objectFit: 'contain' }} />
          </Link>

          {/* CENTRE — links */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 2 }}>
            {NAV_LINKS.map(n => n.to
              ? <Link key={n.label} to={n.to}    style={lk} onMouseEnter={lOn} onMouseLeave={lOff}>{n.label}</Link>
              : <a    key={n.label} href={n.href} style={lk} onMouseEnter={lOn} onMouseLeave={lOff}>{n.label}</a>
            )}
          </div>

          {/* RIGHT — auth */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
            {user ? (
              <Link to="/dashboard" style={pillStyle} onMouseEnter={pillOn} onMouseLeave={pillOff}>
                Dashboard
              </Link>
            ) : (
              <>
                {/* plain Sign In */}
                <Link to="/login" style={lk} onMouseEnter={lOn} onMouseLeave={lOff}>
                  Sign In
                </Link>
                {/* glass pill Sign Up */}
                <Link to="/register" style={{ ...pillStyle, color: W(0.55) }} onMouseEnter={pillOn} onMouseLeave={pillOff}>
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* MOBILE burger */}
          <button
            className="md:hidden"
            onClick={() => setOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, gridColumn: 3, justifySelf: 'end' }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              {open
                ? <path d="M4 4l12 12M16 4L4 16" stroke={W(0.5)} strokeWidth="1.6" strokeLinecap="round"/>
                : <path d="M3 5h14M3 10h14M3 15h14" stroke={W(0.5)} strokeWidth="1.6" strokeLinecap="round"/>}
            </svg>
          </button>
        </div>

        {/* MOBILE drawer */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{ overflow: 'hidden', background: 'rgba(0,0,0,0.96)', borderTop: `1px solid ${W(0.07)}` }}
            >
              <div style={{ padding: '14px 32px 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {NAV_LINKS.map(n => n.to
                  ? <Link key={n.label} to={n.to}    onClick={() => setOpen(false)} style={{ ...lk, display: 'block' }}>{n.label}</Link>
                  : <a    key={n.label} href={n.href} onClick={() => setOpen(false)} style={{ ...lk, display: 'block' }}>{n.label}</a>
                )}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${W(0.07)}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {user ? (
                    <Link to="/dashboard" style={pillStyle} onMouseEnter={pillOn} onMouseLeave={pillOff}>Dashboard</Link>
                  ) : (
                    <>
                      <Link to="/login"    onClick={() => setOpen(false)} style={{ ...lk, display: 'block', textAlign: 'center' }}>Sign In</Link>
                      <Link to="/register" onClick={() => setOpen(false)} style={{ ...pillStyle, justifyContent: 'center' }} onMouseEnter={pillOn} onMouseLeave={pillOff}>Sign Up</Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* spacer so content doesn't hide under fixed nav */}
      <div style={{ height: 0 }} />
    </>
  );
}

/* ─────────────────────────────────────────────
   THREE.JS WEBGL BACKGROUND
───────────────────────────────────────────── */
const VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}`;

const FRAG = `
uniform float uTime;
varying vec2 vUv;

vec2 hash2(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(dot(hash2(i + vec2(0,0)), f - vec2(0,0)), dot(hash2(i + vec2(1,0)), f - vec2(1,0)), u.x),
    mix(dot(hash2(i + vec2(0,1)), f - vec2(0,1)), dot(hash2(i + vec2(1,1)), f - vec2(1,1)), u.x),
    u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 6; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
  return v;
}

void main() {
  vec2 uv = vUv;
  float t = uTime * 0.07;

  vec2 q = vec2(fbm(uv + t * 0.25), fbm(uv + vec2(5.2, 1.3) + t * 0.25));
  vec2 r = vec2(fbm(uv + 4.0*q + vec2(1.7, 9.2) + t*0.18), fbm(uv + 4.0*q + vec2(8.3, 2.8) + t*0.18));
  float f = clamp((fbm(uv + 4.0*r + t*0.09) + 1.0) * 0.5, 0.0, 1.0);

  vec3 dark  = vec3(0.0,  0.0,  0.0);
  vec3 amber = vec3(0.16, 0.08, 0.01);
  vec3 gold  = vec3(0.72, 0.56, 0.22);

  vec3 col = mix(dark, amber, smoothstep(0.05, 0.40, f));
       col = mix(col,  gold,  smoothstep(0.32, 0.72, f));

  /* large soft wave — wide enough to feel like ambience, not a stripe */
  float wave = fbm(vec2(uv.x * 1.4 + t * 0.14, 2.5)) * 0.13
             - fbm(vec2(uv.x * 0.9 - t * 0.10, 6.0)) * 0.09;
  float dist   = abs(uv.y - (0.50 + wave));
  float mask   = smoothstep(0.55, 0.04, dist);   /* fades to black near top/bottom edges */

  /* additional radial vignette so corners stay fully black */
  float vign = smoothstep(0.90, 0.30, length((uv - 0.5) * vec2(1.1, 1.3)));

  col *= mask * vign * 0.01;

  gl_FragColor = vec4(col, 1.0);
}`;

function Background() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;

    const renderer = new THREE.WebGLRenderer({ antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(window.innerWidth, window.innerHeight);
    Object.assign(renderer.domElement.style, { position: 'fixed', inset: 0, zIndex: 0, display: 'block' });
    mount.appendChild(renderer.domElement);

    const scene  = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geo    = new THREE.PlaneGeometry(2, 2);
    const mat    = new THREE.ShaderMaterial({
      uniforms:       { uTime: { value: 0 } },
      vertexShader:   VERT,
      fragmentShader: FRAG,
    });
    scene.add(new THREE.Mesh(geo, mat));

    let raf;
    const animate = () => {
      mat.uniforms.uTime.value += 0.016;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => renderer.setSize(window.innerWidth, window.innerHeight);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      geo.dispose();
      mat.dispose();
    };
  }, []);

  return (
    <>
      <div ref={mountRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        opacity: 0.045,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />
    </>
  );
}

/* ─────────────────────────────────────────────
   SHARED ANIMATION VARIANT
───────────────────────────────────────────── */
const fadeUp = {
  hidden:  { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
// Format: 1234 → "1,234". Returns em-dash placeholder while loading.
const fmtStat = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));

// useCountUp
//
// Animates a numeric value from its previous render to a new target
// using ease-out cubic over `duration` ms. Returns the integer to
// display each frame. Handy for the hero stat trio so the numbers
// feel like they're "counting in" rather than snapping into place
// the moment Supabase responds. Honours prefers-reduced-motion.
function useCountUp(target, duration = 1400) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);

  useEffect(() => {
    if (target == null) return;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setDisplay(target); fromRef.current = target; return; }

    let raf;
    const start = performance.now();
    const from  = fromRef.current;
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (target - from) * eased);
      setDisplay(v);
      fromRef.current = v;
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
}

// Renders a single hero stat with smooth count-up. Falls back to an
// em-dash placeholder while the RPC is still in-flight.
function StatNumber({ value }) {
  const animated = useCountUp(value);
  if (value == null) return <>—</>;
  return <>{Number(animated).toLocaleString('en-US')}</>;
}

// Pull live aggregates (active subs, completed orders, online sessions)
// from the public `landing_stats()` Postgres function. The RPC is the
// only thing the anon role can touch — the underlying tables stay
// locked down by RLS. Online count is refreshed every 30s to match
// the cadence of the heartbeat in v2_online_users.
function useLandingStats() {
  const [stats, setStats] = useState({
    activeSubscriptions: null,
    totalOrders:         null,
    onlineUsers:         null,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      const { data, error } = await supabase.rpc('landing_stats');
      if (cancelled || error || !data) return;
      setStats({
        activeSubscriptions: data.activeSubscriptions ?? 0,
        totalOrders:         data.totalOrders         ?? 0,
        onlineUsers:         data.onlineUsers         ?? 0,
      });
    };
    fetchStats();
    const t = setInterval(fetchStats, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return stats;
}

function Hero() {
  const stats = useLandingStats();
  return (
    <section style={{
      position: 'relative', minHeight: '100vh',
      display: 'flex', alignItems: 'center',
      padding: '120px 0 80px',
    }}>
      {/* Two-column grid */}
      <div style={{
        width: '100%', maxWidth: 1160, margin: '0 auto',
        padding: '0 48px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48,
        alignItems: 'center',
      }}>

        {/* LEFT — text content */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <style>{`
            @keyframes heroPillPulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(200,160,78,0.55); }
              50%       { box-shadow: 0 0 0 6px rgba(200,160,78,0); }
            }
            @media (prefers-reduced-motion: reduce) {
              .hero-pill-dot { animation: none !important; }
            }
          `}</style>
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={0}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(200,160,78,0.07)', border: '1px solid rgba(200,160,78,0.18)',
              borderRadius: 100, padding: '6px 16px', marginBottom: 32,
            }}
          >
            <span
              className="hero-pill-dot"
              style={{
                width: 6, height: 6, borderRadius: '50%', background: GOLD,
                display: 'inline-block', flexShrink: 0,
                animation: 'heroPillPulse 2.4s ease-in-out infinite',
              }}
            />
            <span style={{ fontSize: 11, color: GA(0.85), fontWeight: 600, letterSpacing: '0.08em' }}>ATHENA V2 — NOW AVAILABLE</span>
          </motion.div>

          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            style={{
              margin: '0 0 20px', fontSize: 'clamp(28px, 3vw, 52px)',
              fontWeight: 750, lineHeight: 1.1, letterSpacing: '-0.03em',
              color: '#fff',
            }}
          >
            The only{' '}
            <span style={{ color: GOLD }}>Rust recoil script</span>{' '}
            you'll ever need.
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            style={{
              margin: '0 0 40px', fontSize: 16, lineHeight: 1.7,
              color: W(0.42), maxWidth: 460, fontWeight: 400,
            }}
          >
            The best Rust recoil script on the market. Undetectable, instant setup, and built for players who refuse to lose.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={3}
            style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}
          >
            <Link to="/register"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(200,160,78,0.28)',
                backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(200,160,78,0.1)',
                color: GOLD, fontWeight: 650,
                fontSize: 14, padding: '13px 28px', borderRadius: 10,
                textDecoration: 'none', letterSpacing: '0.01em', transition: 'background .2s, border-color .2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.28)'; }}
            >
              Get Started
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <a href="#features"
              style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
                border: '1px solid rgba(255,255,255,0.09)',
                backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)',
                color: W(0.55), fontWeight: 500, fontSize: 14,
                padding: '13px 28px', borderRadius: 10, textDecoration: 'none',
                letterSpacing: '0.01em', transition: 'background .2s, color .2s, border-color .2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.color = W(0.85); e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)'; e.currentTarget.style.color = W(0.55); e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}
            >
              View Features
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={4}
            style={{
              marginTop: 64, paddingTop: 32,
              borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex', gap: 40, flexWrap: 'wrap',
            }}
          >
            {[
              { value: stats.activeSubscriptions, label: 'Active subscriptions' },
              { value: stats.totalOrders,         label: 'Bought subscriptions' },
              { value: stats.onlineUsers,         label: 'Online users' },
            ].map(({ value, label }) => (
              <div key={label}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                  <StatNumber value={value} />
                </div>
                <div style={{ fontSize: 12, color: W(0.3), marginTop: 3, letterSpacing: '0.03em' }}>{label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — reserved */}
        <div style={{ height: '100%', minHeight: 400 }} />
      </div>

      {/* Bottom fade into features */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 220, background: 'linear-gradient(to bottom, transparent, #000)', pointerEvents: 'none' }} />
    </section>
  );
}

/* ─────────────────────────────────────────────
   FEATURES
───────────────────────────────────────────── */
const FEATURE_LIST = [
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>, title: 'Auto Detection', desc: 'Athena reads your equipped weapon and attachments in real time, switching profiles instantly — no input required.' },
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10 10 0 0 1 2.06 6.06" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/><path d="M1 1l22 22" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/></svg>, title: 'Ghost Mode', desc: 'Ghost Mode instantly hides all traces of Athena from your screen — perfect for streaming or when getting screened by admins.' },
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/></svg>, title: 'Daily Updates', desc: 'Our team pushes patches daily to stay ahead of every game update, keeping Athena undetected and accurate.' },
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><line x1="12" y1="1" x2="12" y2="23" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round"/></svg>, title: 'Affordable Pricing', desc: 'Powerful scripts without the premium price tag. We keep plans accessible while limiting slots to maintain quality.' },
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>, title: 'Zero Performance Impact', desc: 'Built to be as lightweight as possible. Athena runs silently in the background without touching your frame rate.' },
  { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>, title: 'Dedicated Support', desc: 'Run into an issue? We stay with you until it is fully resolved. Our team is committed to keeping every user satisfied.' },
];

/* ─────────────────────────────────────────────
   TILT CARD
───────────────────────────────────────────── */
function TiltCard({ children, style }) {
  const rawMx = useMotionValue(0);
  const rawMy = useMotionValue(0);
  const mx = useSpring(rawMx, { stiffness: 180, damping: 22 });
  const my = useSpring(rawMy, { stiffness: 180, damping: 22 });
  const rotX = useTransform(my, [-0.5, 0.5], [-11, 11]);
  const rotY = useTransform(mx, [-0.5, 0.5], [11, -11]);
  return (
    <motion.div
      style={{ ...style, rotateX: rotX, rotateY: rotY, transformStyle: 'preserve-3d' }}
      onMouseMove={e => {
        const r = e.currentTarget.getBoundingClientRect();
        rawMx.set((e.clientX - r.left) / r.width - 0.5);
        rawMy.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => { rawMx.set(0); rawMy.set(0); }}
    >
      {children}
    </motion.div>
  );
}

const V_LINES = [
  { left: '10%',    delay: '0s'   },
  { left: '22.5%',  delay: '2.2s' },
  { left: '35%',    delay: '4.5s' },
  { left: '50%',    delay: '0.8s' },
  { left: '65%',    delay: '3.1s' },
  { left: '77.5%',  delay: '1.6s' },
  { left: '90%',    delay: '5.2s' },
];
const H_LINES = [0,1,2,3,4,5].map(i => `${Math.round((i/5)*100)}%`);

function Features() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.85', 'end 0.15'] });
  const fastProgress = useTransform(scrollYProgress, [0, 0.65], [0, 1], { clamp: true });
  const lineScale = useSpring(fastProgress, { stiffness: 120, damping: 28 });

  return (
    <section id="features" ref={ref} style={{ position: 'relative', padding: '120px 0', overflow: 'hidden', background: '#000' }}>
      <style>{`
        @keyframes featDrop {
          0%   { top: -20%; }
          100% { top: 110%; }
        }
      `}</style>

      {/* Lines grid */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {V_LINES.map(({ left, delay }, i) => (
          <div key={i} style={{
            position: 'absolute', width: '1px', height: '100%', top: 0, left,
            background: 'rgba(255,255,255,0.055)', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', width: '100%', height: '15vh', left: 0, top: '-20%',
              background: 'linear-gradient(to bottom, rgba(200,160,78,0) 0%, rgba(200,160,78,0.65) 75%, rgba(200,160,78,0.85) 100%)',
              animation: `featDrop 7s ${delay} infinite cubic-bezier(0.4, 0.26, 0, 0.97)`,
              animationFillMode: 'backwards',
            }} />
          </div>
        ))}
        {H_LINES.map((top, i) => (
          <div key={i} style={{
            position: 'absolute', left: 0, right: 0, top, height: '1px',
            background: 'rgba(255,255,255,0.04)',
          }} />
        ))}
      </div>

      {/* Top & bottom fade masks */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 200, background: 'linear-gradient(to bottom, #000 0%, transparent 100%)', zIndex: 3, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: 'linear-gradient(to top, #000 0%, transparent 100%)', zIndex: 3, pointerEvents: 'none' }} />

      {/* Grain noise */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 80, padding: '0 24px' }}
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', margin: '0 0 14px' }}>FEATURES</p>
          <h2 style={{ fontSize: 'clamp(28px, 3.8vw, 46px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
            Everything you need.
          </h2>
        </motion.div>

        <div style={{ position: 'relative', maxWidth: 880, margin: '0 auto', padding: '0 24px', perspective: 1000 }}>
          {/* Growing center line */}
          <motion.div style={{
            position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1,
            background: `linear-gradient(to bottom, transparent, ${GA(0.55)} 12%, ${GA(0.55)} 88%, transparent)`,
            scaleY: lineScale, transformOrigin: 'top',
          }} />

          {FEATURE_LIST.map((f, i) => {
            const left = i % 2 === 0;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: left ? -36 : 36 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: false, margin: '-60px' }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  display: 'flex',
                  justifyContent: left ? 'flex-start' : 'flex-end',
                  marginBottom: i < FEATURE_LIST.length - 1 ? 52 : 0,
                  position: 'relative',
                }}
              >
                <div style={{
                  position: 'absolute', left: '50%', top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 7, height: 7, borderRadius: '50%',
                  background: GOLD, boxShadow: `0 0 10px ${GA(0.8)}`, zIndex: 1,
                }} />
                <div style={{
                  position: 'absolute', top: '50%',
                  [left ? 'right' : 'left']: '50%',
                  width: 32, height: 1,
                  background: `linear-gradient(${left ? 'to left' : 'to right'}, transparent, ${GA(0.28)})`,
                }} />
                <TiltCard style={{
                  width: 'calc(50% - 48px)', padding: '22px',
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.09)',
                  background: `radial-gradient(ellipse 110% 80% at ${left ? '0% 0%' : '100% 0%'}, rgba(200,160,78,0.09) 0%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0.02) 100%)`,
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
                }}>
                  <div style={{ marginBottom: 14 }}>{f.icon}</div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: W(0.85), margin: '0 0 7px', letterSpacing: '-0.01em' }}>{f.title}</h3>
                  <p style={{ fontSize: 13, color: W(0.35), margin: 0, lineHeight: 1.65 }}>{f.desc}</p>
                </TiltCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   TESTIMONIALS
───────────────────────────────────────────── */
const TESTIMONIALS_A = [
  { name: 'xBlaze',    role: 'Lifetime',  stars: 5, text: 'Auto-detection is flawless. Switches profiles before I even finish looting.' },
  { name: 'kr4tos',    role: '30 Days',   stars: 5, text: 'Ghost Mode is a lifesaver when streaming. Never had a close call since.' },
  { name: 'nv_reaper', role: 'Lifetime',  stars: 5, text: 'Setup took maybe 4 minutes. Was already in a server destroying people shortly after.' },
  { name: 'drift404',  role: '30 Days',   stars: 4, text: 'Had a small config issue on first launch. Support sorted it within minutes. Works perfectly now.' },
  { name: 'pulze',     role: '7 Days',    stars: 5, text: 'Cleanest recoil I have ever used. Tried a lot of alternatives and nothing compares.' },
  { name: 'tavrox',    role: 'Lifetime',  stars: 5, text: 'Scope support across all zoom levels is seriously impressive. Irons to 16x just works.' },
  { name: 'krel99',    role: 'Lifetime',  stars: 4, text: 'Was skeptical at first but after a week I went straight for the lifetime. Worth every penny.' },
];
const TESTIMONIALS_B = [
  { name: 'mxlody',   role: 'Lifetime',  stars: 5, text: 'Been using it for over a year. Updates always drop fast and nothing ever breaks.' },
  { name: 'snxpez',   role: '30 Days',   stars: 5, text: 'Patches keep up with the game faster than I expected. Team clearly knows what they are doing.' },
  { name: 'coldrift', role: 'Lifetime',  stars: 5, text: 'Zero FPS drop. Game runs exactly the same as without it. Was not expecting that at all.' },
  { name: 'v0rtex',   role: '7 Days',    stars: 4, text: 'Hit a weird bug after a game update. Opened a ticket and it was fixed same day. Respect.' },
  { name: 'rykzed',   role: '30 Days',   stars: 5, text: 'Eight months straight and I have not had a single issue worth complaining about.' },
  { name: 'phntm',    role: 'Lifetime',  stars: 5, text: 'Runs completely in the background. Zero interference with the game, zero detections.' },
  { name: 'solark',   role: '30 Days',   stars: 4, text: 'Support answered faster than I expected. Issue got resolved and I renewed the same day.' },
];

function TestimonialCard({ name, role, stars, text }) {
  return (
    <div style={{
      width: 290, flexShrink: 0,
      padding: '22px',
      borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.08)',
      background: 'radial-gradient(ellipse 130% 90% at 0% 0%, rgba(200,160,78,0.07) 0%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0.02) 100%)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
    }}>
      <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
        {[...Array(5)].map((_, i) => (
          <svg key={i} width="12" height="12" viewBox="0 0 16 16" fill={i < stars ? GOLD : 'none'} stroke={i < stars ? 'none' : GA(0.3)} strokeWidth="1">
            <path d="M8 1l1.9 4 4.4.6-3.2 3.1.8 4.4L8 11.1l-3.9 2 .8-4.4L1.7 5.6l4.4-.6z"/>
          </svg>
        ))}
      </div>
      <p style={{ fontSize: 13, color: W(0.6), lineHeight: 1.7, margin: '0 0 18px' }}>"{text}"</p>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: W(0.82) }}>{name}</div>
        <div style={{ fontSize: 11, color: role === 'Lifetime' ? GA(0.7) : W(0.28), marginTop: 2 }}>{role}</div>
      </div>
    </div>
  );
}

function Testimonials() {
  return (
    <section style={{ position: 'relative', padding: '120px 0', overflow: 'hidden', background: '#000' }}>
      <style>{`
        @keyframes marqueeL {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marqueeR {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .mq-track-l { animation: marqueeL 38s linear infinite; }
        .mq-track-r { animation: marqueeR 44s linear infinite; }
        .mq-wrap:hover .mq-track-l,
        .mq-wrap:hover .mq-track-r { animation-play-state: paused; }
      `}</style>

      {/* Background — center spotlight + diagonal corner glows */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 75% 55% at 50% 50%, rgba(200,160,78,0.04) 0%, transparent 70%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '45%', height: '65%', background: 'radial-gradient(ellipse at 0% 0%, rgba(200,160,78,0.04) 0%, transparent 65%)' }} />
        <div style={{ position: 'absolute', bottom: 0, right: 0, width: '45%', height: '65%', background: 'radial-gradient(ellipse at 100% 100%, rgba(200,160,78,0.04) 0%, transparent 65%)' }} />
      </div>

      {/* Grain */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Top & bottom section fades */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 200, background: 'linear-gradient(to bottom, #000, transparent)', zIndex: 3, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: 'linear-gradient(to top, #000, transparent)', zIndex: 3, pointerEvents: 'none' }} />

      {/* Left & right marquee edge fades */}
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 140, background: 'linear-gradient(to right, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 140, background: 'linear-gradient(to left, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 64, padding: '0 24px' }}
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', margin: '0 0 14px' }}>TESTIMONIALS</p>
          <h2 style={{ fontSize: 'clamp(28px, 3.8vw, 46px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
            Trusted by players.
          </h2>
        </motion.div>

        <motion.div
          className="mq-wrap"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="mq-track-l" style={{ display: 'flex', gap: 16, width: 'max-content' }}>
              {[...TESTIMONIALS_A, ...TESTIMONIALS_A].map((t, i) => <TestimonialCard key={i} {...t} />)}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="mq-track-r" style={{ display: 'flex', gap: 16, width: 'max-content' }}>
              {[...TESTIMONIALS_B, ...TESTIMONIALS_B].map((t, i) => <TestimonialCard key={i} {...t} />)}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PRICING
───────────────────────────────────────────── */
const PLANS = [
  { name: '7 Days',  tier: '7_days',   price: '$5.00',  period: '/ week',  badge: null,           features: ['All weapons','Auto detection','Scope support','Discord access'] },
  { name: '30 Days', tier: '30_days',  price: '$10.00', period: '/ month', badge: 'Most Popular', features: ['All weapons','Auto detection','Scope support','Discord access','Priority support','Beta access'] },
  { name: 'Lifetime',tier: 'lifetime', price: '$20.00', period: 'one-time',badge: null,           features: ['All weapons','Auto detection','Scope support','Discord access','Priority support','All future updates'] },
];

function StockBadge({ stock }) {
  const out   = stock === 0;
  const low   = stock > 0 && stock <= 10;
  const color = out ? '#ef4444' : low ? '#f59e0b' : '#4ade80';
  const label = out ? 'OUT OF STOCK' : `${stock} IN STOCK`;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 10,
      background: out ? 'rgba(239,68,68,0.08)' : low ? 'rgba(245,158,11,0.08)' : 'rgba(74,222,128,0.07)',
      border: `1px solid ${out ? 'rgba(239,68,68,0.2)' : low ? 'rgba(245,158,11,0.2)' : 'rgba(74,222,128,0.15)'}`,
    }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: color, boxShadow: `0 0 5px ${color}`, flexShrink: 0 }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: out ? 'rgba(239,68,68,0.8)' : low ? 'rgba(245,158,11,0.8)' : 'rgba(74,222,128,0.6)', whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>{label}</span>
    </div>
  );
}

function Pricing() {
  const navigate = useNavigate();
  const [stockCounts,     setStockCounts]     = useState({ '7_days': null, '30_days': null, 'lifetime': null });
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError,   setCheckoutError]   = useState('');

  useEffect(() => {
    supabase.rpc('get_key_stock_counts').then(({ data }) => {
      if (data) {
        const c = { '7_days': 0, '30_days': 0, 'lifetime': 0 };
        data.forEach(r => { c[r.tier] = r.count || 0; });
        setStockCounts(c);
      }
    });
  }, []);

  // Click handler for the Get Started buttons. Skips the
  // signup detour for users who are already authenticated and
  // sends them straight into Stripe checkout. supabase.functions
  // .invoke is preferred over a hand-rolled fetch because it
  // refreshes the JWT before sending — the bare fetch path used
  // to 401 once Supabase rotated the project's signing keys.
  const handleGetStarted = async (tier) => {
    if (stockCounts[tier] === 0) return;
    setCheckoutError('');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate(`/register?plan=${tier}`);
      return;
    }
    setCheckoutLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', { body: { tier } });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(data?.error || 'Failed to start checkout.');
      }
    } catch (err) {
      console.error('checkout:', err);
      setCheckoutError(err.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };
  return (
    <section id="pricing" style={{ position: 'relative', padding: '120px 0', overflow: 'hidden', background: '#000' }}>

      {/* Background — upward radial glow */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 90% 55% at 50% 100%, rgba(200,160,78,0.07) 0%, transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 50% 30% at 50% 100%, rgba(200,160,78,0.05) 0%, transparent 55%)' }} />
      </div>

      {/* Grain */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Top & bottom fades */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 200, background: 'linear-gradient(to bottom, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: 'linear-gradient(to top, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 3, maxWidth: 1000, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 72 }}
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', margin: '0 0 14px' }}>PRICING</p>
          <h2 style={{ fontSize: 'clamp(28px, 3.8vw, 46px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.025em' }}>
            Simple, transparent pricing.
          </h2>
        </motion.div>

        <style>{`
          @keyframes cardFloat {
            0%, 100% { transform: translateY(0px); }
            50%       { transform: translateY(-11px); }
          }
          @keyframes shadowFloat {
            0%, 100% { transform: scaleX(1) scaleY(1); opacity: 1; }
            50%       { transform: scaleX(0.62) scaleY(0.35); opacity: 0.2; }
          }
        `}</style>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, alignItems: 'stretch' }}>
          {PLANS.map((plan, i) => {
            const featured = !!plan.badge;
            const dur  = `${3.2 + i * 0.6}s`;
            const del  = `${i * 0.55}s`;
            return (
              <motion.div
                key={plan.name}
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.3}
                whileHover={{ scale: 1.015 }}
                transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                style={{ position: 'relative', height: '100%', paddingBottom: 28 }}
              >
                {/* Tracking glow shadow */}
                <div style={{
                  position: 'absolute', bottom: 4, left: '12%', right: '12%', height: 22,
                  background: `radial-gradient(ellipse at 50% 100%, ${GA(featured ? 0.75 : 0.4)} 0%, transparent 70%)`,
                  filter: 'blur(10px)',
                  transformOrigin: 'center bottom',
                  pointerEvents: 'none',
                  animation: `shadowFloat ${dur} ease-in-out ${del} infinite`,
                }} />

                {/* Floating card */}
                <div style={{
                  position: 'relative',
                  padding: '36px 28px 116px',
                  borderRadius: 16,
                  height: '100%',
                  boxSizing: 'border-box',
                  border: featured ? `1px solid ${GA(0.4)}` : '1px solid rgba(255,255,255,0.07)',
                  background: featured
                    ? `radial-gradient(ellipse 130% 80% at 50% 0%, rgba(200,160,78,0.14) 0%, rgba(200,160,78,0.04) 50%, rgba(255,255,255,0.02) 100%)`
                    : i === 0
                      ? `radial-gradient(ellipse 120% 80% at 0% 0%, rgba(200,160,78,0.09) 0%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0.02) 100%)`
                      : `radial-gradient(ellipse 120% 80% at 100% 0%, rgba(200,160,78,0.09) 0%, rgba(255,255,255,0.03) 55%, rgba(255,255,255,0.02) 100%)`,
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  boxShadow: featured
                    ? `0 0 40px rgba(200,160,78,0.08), 0 8px 28px rgba(0,0,0,0.5), inset 0 1px 0 ${GA(0.18)}`
                    : '0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                  display: 'flex', flexDirection: 'column',
                  animation: `cardFloat ${dur} ease-in-out ${del} infinite`,
                }}>
                  {/* Top-right stock pill */}
                  <div style={{ position: 'absolute', top: 16, right: 16 }}>
                    <StockBadge stock={stockCounts[plan.tier] ?? 0} />
                  </div>

                  {featured && (
                    <span style={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: GOLD, color: '#000', fontSize: 10, fontWeight: 700,
                      padding: '4px 14px', borderRadius: 100, letterSpacing: '0.07em', whiteSpace: 'nowrap',
                    }}>{plan.badge}</span>
                  )}

                  <p style={{ fontSize: 10, fontWeight: 700, color: featured ? GA(0.6) : W(0.22), margin: '0 0 14px', letterSpacing: '0.12em' }}>{plan.name.toUpperCase()}</p>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 44, fontWeight: 750, color: featured ? '#fff' : W(0.85), letterSpacing: '-0.03em', lineHeight: 1 }}>{plan.price}</span>
                    <span style={{ fontSize: 12, color: W(0.25) }}>{plan.period}</span>
                  </div>

                  <div style={{ height: 1, background: featured ? GA(0.15) : 'rgba(255,255,255,0.06)', margin: '22px 0' }} />

                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {plan.features.map(f => (
                      <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: featured ? W(0.65) : W(0.38) }}>
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M3 8l4 4 6-8" stroke={GOLD} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button onClick={() => handleGetStarted(plan.tier)}
                    disabled={stockCounts[plan.tier] === 0 || checkoutLoading === plan.tier}
                    style={{
                    position: 'absolute', bottom: 28, left: 28, right: 28,
                    display: 'block', textAlign: 'center',
                    padding: '12px', borderRadius: 10, fontWeight: 600, fontSize: 13.5,
                    fontFamily: 'inherit',
                    textDecoration: 'none', letterSpacing: '0.01em', transition: 'background .2s, border-color .2s',
                    background: featured
                      ? 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'
                      : 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
                    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 ' + (featured ? 'rgba(200,160,78,0.1)' : 'rgba(255,255,255,0.06)'),
                    color: featured ? GOLD : W(0.55),
                    border: featured ? '1px solid rgba(200,160,78,0.28)' : '1px solid rgba(255,255,255,0.09)',
                    cursor: stockCounts[plan.tier] === 0 ? 'not-allowed' : 'pointer',
                    opacity: stockCounts[plan.tier] === 0 ? 0.35 : 1,
                  }}
                    onMouseEnter={e => { if (stockCounts[plan.tier] > 0) { if (featured) { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.45)'; } else { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; } } }}
                    onMouseLeave={e => { if (stockCounts[plan.tier] > 0) { if (featured) { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.28)'; } else { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; } } }}
                  >
                    {stockCounts[plan.tier] === 0
                      ? 'Out of Stock'
                      : checkoutLoading === plan.tier
                        ? 'Loading…'
                        : 'Get Started'}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FAQ
───────────────────────────────────────────── */
const FAQS = [
  {
    q: 'Is Athena undetectable?',
    a: 'Yes. Every mouse input is routed through our signed kernel-level driver instead of standard Windows APIs, which keeps Athena invisible to anti-cheat software like EAC. We continuously monitor detection vectors and push updates the moment anything changes.',
  },
  {
    q: 'Why am I getting an HWID mismatch when I log in?',
    a: 'Your license is locked to the first device you sign in on. If you see the "HWID Not Match" screen, it means Athena is detecting a different PC. We do not allow multi-device use — open a ticket in our Discord if you need an HWID reset.',
  },
  {
    q: 'Why am I seeing a "You\u2019re banned" screen?',
    a: 'That message means your account has been banned for violating our terms of service. If you believe it was a mistake, you can appeal by opening a ticket in our Discord and our team will review the case.',
  },
  {
    q: 'Do you offer refunds?',
    a: 'As a rule, all sales are final. The only exception is a genuine technical issue on our end that we are unable to fix — in that case we offer a full refund within 24 hours of the key being purchased and activated. After 24 hours no refunds are issued.',
  },
  {
    q: 'Can I buy a key for a friend?',
    a: 'Key sharing is not allowed, but gifting is. Complete the purchase yourself, then open a ticket in our Discord with the order ID and your friend\u2019s username or email, and we\u2019ll transfer the activation to their account.',
  },
  {
    q: 'Athena is not working on my PC — what do I do?',
    a: 'A small number of off-brand and older mice from third-party vendors can have compatibility issues with our driver. Open a ticket in our Discord, share your setup, and our support team will work with you to get it running.',
  },
  {
    q: 'I got banned on a server — can I get a replacement?',
    a: 'Athena is undetectable to EAC, but it cannot hide you from a human server admin who is spectating. Bans handed out by server admins are outside our control and do not qualify for refunds or replacements.',
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '22px 0', background: 'none', border: 'none', cursor: 'pointer',
          color: W(0.8), fontSize: 14.5, fontWeight: 500, letterSpacing: '-0.01em',
          textAlign: 'left', gap: 16,
        }}
      >
        {q}
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.2 }}
          style={{ color: W(0.28), fontSize: 20, lineHeight: 1, flexShrink: 0, display: 'inline-block' }}>+</motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{ margin: '0 0 24px', fontSize: 13.5, color: W(0.35), lineHeight: 1.75, maxWidth: 580 }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FAQ() {
  return (
    <section id="faq" style={{ position: 'relative', padding: '120px 0', overflow: 'hidden', background: '#000' }}>

      {/* Dot grid */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }} />

      {/* Dark band that travels top → bottom, fading dots out as it passes */}
      <style>{`
        @keyframes faqWave {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(160%);  }
        }
      `}</style>
      <div style={{
        position: 'absolute', left: 0, right: 0, height: '55%', pointerEvents: 'none', zIndex: 1,
        background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.88) 25%, rgba(0,0,0,0.88) 75%, transparent 100%)',
        animation: 'faqWave 5s linear infinite',
      }} />

      {/* Grain */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, opacity: 0.045,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Top & bottom fades */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 200, background: 'linear-gradient(to bottom, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: 'linear-gradient(to top, #000, transparent)', zIndex: 2, pointerEvents: 'none' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 3, maxWidth: 700, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 64 }}
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', margin: '0 0 14px' }}>FAQ</p>
          <h2 style={{ fontSize: 'clamp(28px, 3.8vw, 46px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.025em' }}>
            Common questions.
          </h2>
        </motion.div>
        {FAQS.map((item, i) => (
          <motion.div
            key={item.q}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-40px' }}
            custom={i * 0.5}
          >
            <FAQItem {...item} />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CTA
───────────────────────────────────────────── */
function CTA() {
  return (
    <section style={{ position: 'relative', background: '#000', overflow: 'hidden' }}>

      {/* Curved top — fills above the curve with black (FAQ color), gold stroke makes the line visible */}
      <svg viewBox="0 0 1440 100" preserveAspectRatio="none"
        style={{ display: 'block', width: '100%', height: 100, position: 'relative', zIndex: 2 }}>
        <path d="M0,0 C480,100 960,100 1440,0 L1440,0 L0,0 Z" fill="#000"/>
        <path d="M0,0 C480,100 960,100 1440,0" fill="none" stroke="rgba(200,160,78,0.12)" strokeWidth="1"/>
      </svg>

      {/* Subtle top glow — only lights up the curve, fades out fast */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '28%', pointerEvents: 'none',
        background: 'radial-gradient(ellipse 55% 100% at 50% 0%, rgba(200,160,78,0.055) 0%, transparent 100%)',
      }} />

      {/* Grain */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Bottom fade into footer */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 120, background: 'linear-gradient(to top, #000, transparent)', pointerEvents: 'none' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '80px 24px 110px', maxWidth: 680, margin: '0 auto' }}>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: GOLD, letterSpacing: '0.12em', margin: '0 0 20px' }}>GET STARTED</p>
          <h2 style={{ fontSize: 'clamp(32px, 5vw, 58px)', fontWeight: 700, color: '#fff', margin: '0 0 20px', letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            Ready to gain the edge?
          </h2>
          <p style={{ fontSize: 14, color: W(0.38), margin: '0 0 44px', lineHeight: 1.7 }}>
            Join thousands of players already using Athena. Setup takes under five minutes.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              padding: '13px 32px', borderRadius: 10, fontWeight: 600, fontSize: 14,
              background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)',
              border: '1px solid rgba(200,160,78,0.28)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(200,160,78,0.1)',
              color: GOLD, textDecoration: 'none', letterSpacing: '0.01em', transition: 'background .2s, border-color .2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.18) 0%, rgba(200,160,78,0.08) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(200,160,78,0.11) 0%, rgba(200,160,78,0.04) 55%, rgba(255,255,255,0.02) 100%)'; e.currentTarget.style.borderColor = 'rgba(200,160,78,0.28)'; }}
            >Get Started</Link>
            <a href="#pricing" style={{
              padding: '13px 32px', borderRadius: 10, fontWeight: 600, fontSize: 14,
              background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)',
              border: '1px solid rgba(255,255,255,0.09)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)',
              color: W(0.55), textDecoration: 'none', letterSpacing: '0.01em', transition: 'background .2s, border-color .2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 55%, rgba(255,255,255,0.03) 100%)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.color = W(0.85); }}
              onMouseLeave={e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.color = W(0.55); }}
            >View Pricing</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FOOTER — imported from components/Footer.js
───────────────────────────────────────────── */
export default function Home() {
  return (
    <div style={{ position: 'relative', background: '#000000', minHeight: '100vh', overflowX: 'hidden' }}>
      <Background />
      <div style={{ position: 'relative', zIndex: 2 }}>
        <Navbar />
        <Hero />
        <Features />
        <Testimonials />
        <Pricing />
        <FAQ />
        <CTA />
        <Footer />
      </div>
    </div>
  );
}
